import { useFontRender } from "@/components/FontRender";
import { IMicrositeComponent, MComponentCV } from "@/types/microsite";
import { Fragment, Suspense, useEffect, useRef, useState } from "react";

import Button from "@/components/Button";
import FormatedText from "@/components/FormatedText";
import { useModal } from "@/components/Modal";

import { useComponent } from "@/hooks/useMicrositeComponent";
import { handleApiErr, useAPI } from "@/lib/api";
import { cn } from "@/lib/utils";
import { micrositePrefix } from "@/vars/assetUrl";
import {
  EnvelopeIcon,
  EyeIcon,
  HomeIcon,
  LockClosedIcon,
  PhoneIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import dayjs from "dayjs";
import dynamic from "next/dynamic";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import {
  DummyIcon,
  LinkComponentStyle,
  StyledIcon,
  StyledLink,
  TextOuter,
} from "./LinkComponents";
import { useProtected } from "./ProtectedModal";
import { TextComponentStyle } from "./Text";

const RenderIcon = dynamic(() => import("@/components/RenderIcon"), {
  ssr: false,
});

const CVComponent = {
  kind: "CV",
  Render: ({ microsite, isBuilder, id, data, isFocus }) => {
    const api = useAPI();
    const withIcon = !!data.icon;

    const [intModal, setIntModal] = useState(false);

    const popupRef = useRef<Window | null>(null);

    const [needPassword, setNeedPassword] = useState(data.needPassword);
    const [isRevealed, setIsRevealed] = useState(
      isBuilder ? true : data._isRevealed
    );

    const cvModal = useModal({ isOpen: false });

    const protectedProp = useProtected({
      micrositeId: microsite?.linkId,
      componentId: id,
      message: data.passwordMessage,
      isBuilder,
      onSuccess: (data) => {
        setData(data);
        cvModal.open();
        setNeedPassword(false);
        setIsRevealed(true);
      },
    });

    const revealedInfoProp = useProtected({
      micrositeId: microsite?.linkId,
      componentId: id,
      revealedInfo: true,
      message: data.passwordMessage,
      isBuilder,
      onSuccess: (data) => {
        setData(data);
        setIsRevealed(true);
      },
    });

    const [
      {
        fullName,
        phone,
        email,
        address,
        description,
        summary,
        experiences,
        educations,
        skills,
      },
      setData,
    ] = useState({
      fullName: data.fullName,
      phone: data.phone,
      email: data.email,
      address: data.address,
      description: data.description,
      summary: data.summary,
      experiences: data.experiences || [],
      educations: data.educations || [],
      skills: data.skills || [],
    });

    const hasButtonShadow = !!microsite?.style?.buttonShadowColor;
    const buttonType = microsite?.style?.buttonType || "elevate";
    const buttonBorderColor = microsite?.style?.buttonBorderColor;

    const { fontStyle, RenderFontStyle } = useFontRender({
      textFont: data.textFont,
    });

    const { focusTo } = useComponent();

    const handleClick = (e) => {
      if (isBuilder) {
        e.preventDefault();
        cvModal.open();
        focusTo(id as string);
        return;
      }

      if (needPassword) {
        protectedProp.onRequest();
      } else {
        cvModal.open();
      }
    };

    const onReceive = (e) => {
      const _data = e.data;

      if (_data.from === "microsite_cv" && _data.action === "READY") {
        popupRef.current?.postMessage(
          {
            from: "microsite_cv",
            action: "SYNC",
            data: {
              fullName,
              phone,
              email,
              address,
              description,
              summary,
              experiences,
              educations,
              skills,
              slug: microsite?.slug,
            },
          },
          "*"
        );
      }
    };

    useEffect(() => {
      window.addEventListener("message", onReceive);
      return () => {
        window.removeEventListener("message", onReceive);
      };
    }, [
      fullName,
      phone,
      email,
      address,
      description,
      summary,
      experiences,
      educations,
      skills,
    ]);

    const onDownload = () => {
      const winHeight = 900;
      const winWidth = 800;
      const winTop = screen.height / 2 - winHeight / 2;
      const winLeft = screen.width / 2 - winWidth / 2;
      popupRef.current = window.open(
        isBuilder ? "/preview/cv" : `${micrositePrefix}@preview/cv`,
        "cv-container",
        "top=" +
          winTop +
          ",left=" +
          winLeft +
          ",toolbar=0,status=0,width=" +
          winWidth +
          ",height=" +
          winHeight
      );
    };

    const revealedInformation = async (values) => {
      try {
        const data = await api.post(`/microsite/cv`, {
          micrositeId: microsite?.micrositeId || microsite?._id,
          componentId: id,
          captcha: values.captcha,
        });
        setData(data.data);
        setIsRevealed(true);
      } catch (e) {
        handleApiErr(e, {
          toast,
          t: (v) => v,
          setErrors: null,
          onError: null,
        });
      }
    };

    useEffect(() => {
      setData({
        fullName: data.fullName,
        phone: data.phone,
        email: data.email,
        address: data.address,
        description: data.description,
        summary: data.summary,
        experiences: data.experiences || [],
        educations: data.educations || [],
        skills: data.skills || [],
      });
    }, [
      data.fullName,
      data.phone,
      data.email,
      data.address,
      data.description,
      data.summary,
      data.experiences,
      data.educations,
      data.skills,
    ]);

    useEffect(() => {
      setIntModal(true);
    }, []);

    useEffect(() => {
      if (isFocus) {
        setTimeout(() => {
          handleClick({
            preventDefault: () => {
              //...
            },
          });
        }, 1000);
      }
    }, [isFocus]);

    return (
      <Fragment>
        {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
        {RenderFontStyle}
        <LinkComponentStyle id={`mcomp-${id}`}>
          {" "}
          <StyledLink
            buttonColor={data.buttonColor}
            buttonEffect={data.buttonEffect}
            buttonType={buttonType}
            hasButtonShadow={hasButtonShadow}
            buttonBorderColor={buttonBorderColor}
            isButton={true}
            rel="noopener noreferrer"
            onClick={handleClick}
          >
            {withIcon ? (
              <StyledIcon buttonType={buttonType} className="">
                <Suspense>
                  <RenderIcon icon={data.icon} iconSize="var(--icon-width)" />
                </Suspense>
              </StyledIcon>
            ) : (
              <DummyIcon textPosition={data.textPosition} left />
            )}
            <TextOuter className="">
              <TextComponentStyle
                style={fontStyle}
                position={data.textPosition}
              >
                <FormatedText value={data.content} singleLine />
              </TextComponentStyle>
            </TextOuter>
            {needPassword ? (
              <StyledIcon buttonType={buttonType} className="">
                <LockClosedIcon className="w-5" />
              </StyledIcon>
            ) : (
              <DummyIcon textPosition={data.textPosition} right />
            )}
          </StyledLink>
        </LinkComponentStyle>

        {intModal ? (
          createPortal(
            <Fragment>
              <div
                className={cn(
                  "transition-all",
                  "fixed top-0 left-0 w-full h-full bg-black/20 z-50",
                  cvModal.isOpen ? "" : "pointer-events-none opacity-0"
                )}
              >
                <div
                  className={cn(
                    "top-0 bottom-0 absolute pt-16 w-full transition-all duration-300",
                    cvModal.isOpen ? "translate-y-0" : "translate-y-full"
                  )}
                >
                  <div
                    className="top-0 bottom-0 left-0 absolute w-full dummy"
                    onClick={cvModal.close}
                  />

                  <div className="z-10 relative flex flex-col bg-white mx-auto rounded-t-xl max-w-[700px] h-full font-sans">
                    <header className="flex items-center p-2 border-b font-bold text-center">
                      <h2 className="pl-14 w-full text-lg">Curriculum Vitae</h2>
                      <button
                        className="hover:bg-black/10 p-1 rounded-full transition-all"
                        onClick={cvModal.close}
                      >
                        <XMarkIcon className="w-8" />
                      </button>
                    </header>
                    <div className="flex-1 p-3 overflow-x-hidden overflow-y-auto">
                      <h1 className="font-bold text-xl">{fullName}</h1>
                      <p className="mb-2 font-semibold text-gray-600">
                        {description}
                      </p>
                      <p className="flex gap-4 text-sm">
                        {email ? (
                          <span>
                            <EnvelopeIcon className="inline-block w-3" />{" "}
                            {isRevealed ? email : <span>********@*****</span>}
                          </span>
                        ) : (
                          <Fragment />
                        )}
                        {phone ? (
                          <span>
                            <PhoneIcon className="inline-block -mt-1 w-3" />{" "}
                            {isRevealed ? phone : <span>************</span>}
                          </span>
                        ) : (
                          <Fragment />
                        )}
                      </p>
                      <p className="py-2 text-sm">
                        <HomeIcon className="inline-block -mt-1 w-3" />{" "}
                        {isRevealed ? (
                          address
                        ) : (
                          <span>**** ********* ***** ****</span>
                        )}
                      </p>

                      {!isRevealed && (
                        <div className="mb-2 text-sm">
                          <Button
                            variant="blue"
                            className="flex text-xs"
                            size="sm"
                            type="button"
                            onClick={revealedInfoProp.onRequest}
                          >
                            <EyeIcon className="mr-1 w-3" />
                            Reveal Information above
                          </Button>
                        </div>
                      )}
                      <hr />
                      <p className="mt-2 mb-2 text-sm">{summary}</p>
                      <hr />

                      {experiences?.length > 0 && (
                        <Fragment>
                          <h2 className="mt-4 font-semibold text-lg">
                            Working Experience
                          </h2>

                          <ul>
                            {experiences?.map((exp, i) => (
                              <li
                                key={i}
                                className="py-3 border-b last:border-b-0"
                              >
                                <div className="flex">
                                  <div className="flex-1 shrink-0">
                                    <p className="font-bold">{exp.name}</p>
                                    <p className="text-sm">{exp.position}</p>
                                  </div>
                                  <div className="mt-1 text-xs text-right shrink-0">
                                    <p>
                                      {dayjs(exp.startDate || undefined).format(
                                        "DD MMMM YYYY"
                                      )}{" "}
                                      &mdash;{" "}
                                      {exp.currentlyHere
                                        ? "Present"
                                        : dayjs(
                                            exp.endDate || undefined
                                          ).format("DD MMMM YYYY")}
                                    </p>
                                    {exp.location ? <p>{exp.location}</p> : ""}
                                  </div>
                                </div>
                                <p className="mt-1 text-sm">
                                  {exp.description}
                                </p>
                              </li>
                            ))}
                          </ul>

                          <hr className="mx-auto my-6 border-b-2 w-10" />
                        </Fragment>
                      )}

                      {educations?.length > 0 && (
                        <Fragment>
                          <h2 className="mt-4 font-semibold text-lg">
                            Education
                          </h2>

                          <ul>
                            {educations?.map((exp, i) => (
                              <li
                                key={i}
                                className="py-3 border-b last:border-b-0"
                              >
                                <div className="flex">
                                  <div className="flex-1 shrink-0">
                                    <p className="font-bold">
                                      {exp.name}{" "}
                                      {exp.gpa ? `(GPA ${exp.gpa})` : ""}
                                    </p>
                                    <p className="text-sm">{exp.position}</p>
                                  </div>
                                  <div className="mt-1 text-xs text-right shrink-0">
                                    <p>
                                      {dayjs(exp.startDate || undefined).format(
                                        "DD MMMM YYYY"
                                      )}{" "}
                                      &mdash;{" "}
                                      {exp.currentlyHere
                                        ? "Present"
                                        : dayjs(
                                            exp.endDate || undefined
                                          ).format("DD MMMM YYYY")}
                                    </p>
                                    {exp.location ? <p>{exp.location}</p> : ""}
                                  </div>
                                </div>
                                <p className="mt-1 text-sm">
                                  {exp.description}
                                </p>
                              </li>
                            ))}
                          </ul>

                          <hr className="mx-auto my-6 border-b-2 w-10" />
                        </Fragment>
                      )}

                      {skills?.length > 0 && (
                        <Fragment>
                          <h2 className="mt-4 font-semibold text-lg">Skills</h2>

                          <ul>
                            {skills?.map((exp, i) => (
                              <li
                                key={i}
                                className="py-3 border-b last:border-b-0"
                              >
                                <p className="font-bold text-sm">{exp.title}</p>
                                <p className="text-sm">{exp.description}</p>
                              </li>
                            ))}
                          </ul>
                        </Fragment>
                      )}
                    </div>
                    <div className="p-3 border-t">
                      <Button
                        variant="blue"
                        className="w-full"
                        onClick={onDownload}
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Fragment>,
            document.body
          )
        ) : (
          <Fragment />
        )}
      </Fragment>
    );
  },
} as IMicrositeComponent<MComponentCV>;

export default CVComponent;
